import camelcase from "camelcase-keys";
import clsx from "clsx";
import { FunctionComponent } from "react";

import { RenderWhen } from "../../../../../components";
import cardStyles from "../../../../../components/subheader/card/card.module.scss";
import { enums, settings } from "../../../../../shim/jsbridge";
import {
  getMenuItemForPlatform,
  isLocaleAvailableForPlatform
} from "../../../../../utils";
import { AnalysisTypeInfo } from "../../../../header/hooks/usePrepareReanalyzeData";
import ReanalyzeCardContainer from "../reanalyze-card-container/ReanalyzeCardContainer";
import ReanalyzeDropdown from "../reanalyze-dropdown/ReanalyzeDropdown";
import ReanalyzeFormInput, {
  LAYOUT_TYPE
} from "../reanalyze-form-input/ReanalyzeFormInput";
import {
  DEFAULT_MENU_STYLES,
  RlcsErrors,
  RlcsParams
} from "../reanalyze-form/";
import ReanalyzeTextInput from "../reanalyze-text-input/ReanalyzeTextInput";
import GeolocationMenuItem, {
  GEOLOCATION_TO_LABEL
} from "../../geolocationmenuitem/GeolocationMenuItem";
import RlcsQuotaLimitInfo from "./RlcsQuotaLimitInfo";
import { useRlcsQuotaLimitInfo } from "../../../hooks";

type ParamValue = string | number | undefined;
export type InteractiveParamChangeValue = Record<string, ParamValue>;

interface RlcsConfigurationInputProps {
  isFileAnalysis: boolean;
  isSelected: boolean;
  analysisInfo?: AnalysisTypeInfo;
  params: RlcsParams;
  disabled?: boolean;
  errors?: RlcsErrors;
  onSelect: () => void;
  onParamChange: (value: Record<string, ParamValue>) => void;
}

const PLATFORMS_SUPPORTED_BY_INTERACTIVE_ANALYSIS = [
  enums.platforms.WINDOWS_10,
  enums.platforms.WINDOWS_11
];

const filterByAnalysisMode = ([, platform]: [string, string]) =>
  PLATFORMS_SUPPORTED_BY_INTERACTIVE_ANALYSIS.includes(platform);

const InteractiveAnalysisInput: FunctionComponent<
  RlcsConfigurationInputProps
> = ({
  isFileAnalysis,
  isSelected,
  params,
  errors,
  analysisInfo,
  disabled = false,
  onSelect,
  onParamChange
}) => {
  if (!settings.FEATURE_RL_INTERACTIVE_ANALYSIS) return null;

  const { isHighUsage, isMaxedOut, displayPercent, showLimitInfo } =
    useRlcsQuotaLimitInfo();

  const {
    enabled,
    queueFilled,
    isLoadingAnalysisStatus,
    additionalInformation,
    lastCompletedOn,
    displayStatus
  } = camelcase(analysisInfo ?? {});

  const inputDisabled =
    !enabled || disabled || queueFilled || isLoadingAnalysisStatus;

  const shouldShowTooltip = inputDisabled && !!additionalInformation;

  const helpText =
    additionalInformation ?? (!lastCompletedOn ? displayStatus : undefined);

  return (
    <ReanalyzeCardContainer
      classNameInnerContainer={"p-0 border-1 mb-10"}
      disabled={inputDisabled}
      selected={isSelected}
      hoverable={false}
      layout={LAYOUT_TYPE.NONE}
      tooltipMessage={
        shouldShowTooltip && !showLimitInfo ? additionalInformation : undefined
      }
      classNameOuterContainer="mb-10"
    >
      <div className="w-100">
        <div
          className={clsx(
            "py-15 px-20",
            inputDisabled ? "cursor-not-allowed" : "cursor-pointer",
            cardStyles.card_hoverable
          )}
          onClick={() => {
            if (inputDisabled) {
              return;
            }
            onSelect();
          }}
        >
          <ReanalyzeFormInput
            name="rl_dynamic_analysis_interactive"
            label="Interactive Analysis"
            labelIcon="rl-logo-square"
            labelIconSize={18}
            helpImportant={shouldShowTooltip ? undefined : helpText}
            timestamp={lastCompletedOn}
            disabled={inputDisabled}
            layout={LAYOUT_TYPE.ROW}
            checked={isSelected}
            isLoadingAnalysisStatus={isLoadingAnalysisStatus}
            trailingLabelContent={
              showLimitInfo ? (
                <RlcsQuotaLimitInfo
                  isHighUsage={isHighUsage}
                  isMaxedOut={isMaxedOut}
                  displayPercent={displayPercent}
                  messageOnly
                />
              ) : undefined
            }
          />
        </div>
        <RenderWhen condition={isSelected}>
          <div className="d-flex flex-column border-top border-primary">
            <div className="d-flex flex-row border-top">
              <ReanalyzeDropdown<string>
                filterValue={params?.platform}
                fieldName="PLATFORM"
                className="flex-grow-1 w-100"
                options={Object.entries(enums.platforms)
                  .filter(filterByAnalysisMode)
                  .map(([key, value]) => getMenuItemForPlatform(key, value))}
                filterTitle="platform"
                menuStyles={DEFAULT_MENU_STYLES}
                onFilterChange={({ value }) => {
                  onParamChange({ platform: value });
                }}
                disabled={inputDisabled}
              />
              <ReanalyzeDropdown<string>
                filterValue={params?.locale}
                filterTitle="locale"
                className="flex-grow-1 w-100 border-end border-start border-color-gray-84"
                options={Object.values(enums.locales).map(value => ({
                  label: value as string,
                  value: value as string
                }))}
                fieldName="locale"
                menuStyles={DEFAULT_MENU_STYLES}
                onFilterChange={({ value }) => {
                  onParamChange({ locale: value });
                }}
                disabled={
                  !isLocaleAvailableForPlatform(params?.platform) ||
                  inputDisabled
                }
              />
              <ReanalyzeDropdown<string>
                filterValue={params?.geolocation}
                filterTitle="geolocation"
                className="flex-grow-1 w-100"
                options={Object.values(enums.geolocations).map(value => ({
                  label: GEOLOCATION_TO_LABEL[value as string],
                  value: value as string
                }))}
                fieldName="geolocation"
                menuStyles={DEFAULT_MENU_STYLES}
                onFilterChange={({ value }) => {
                  onParamChange({ geolocation: value });
                }}
                formatLabel={menuItem => (
                  <GeolocationMenuItem value={menuItem?.value} />
                )}
                disabled={inputDisabled}
              />
            </div>
            <div className="d-flex flex-row border-top">
              <ReanalyzeTextInput
                value={params?.execution_timeout}
                onChange={value => {
                  const timeout = value ? parseInt(value, 10) : undefined;
                  onParamChange({ execution_timeout: timeout });
                }}
                disabled={inputDisabled}
                className="flex-grow-1 w-100 border-end border-start border-color-gray-84"
                fieldLabel="Execution Timeout"
                inputLabel="seconds"
                type="number"
                placeholder="200"
                tooltip="Enter a value between 30s and 500s. Default timeout value will be used if not provided (200s)."
                error={errors?.execution_timeout}
              />
              <ReanalyzeTextInput
                value={params?.sample_name ?? ""}
                onChange={value => onParamChange?.({ sample_name: value })}
                disabled={inputDisabled || !isFileAnalysis}
                className="flex-grow-1 w-100 border-start border-color-gray-84"
                fieldLabel="File name"
                type="text"
                placeholder="Let the system decide"
                tooltip="Smart Sample Naming algorithm will be used if not provided."
              />
            </div>
          </div>
        </RenderWhen>
      </div>
    </ReanalyzeCardContainer>
  );
};

export default InteractiveAnalysisInput;
